import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { contractsApi, ContractType, CreateContractPayload } from '../../api/contractsApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { apartmentsApi, Apartment } from '../../api/apartmentsApi';
import { feeCategoriesApi, FeeCategory } from '../../api/feeCategoriesApi';
import { useAuth } from '../../contexts/AuthContext';
import { applyDefaultBuildingSelection } from '../../utils/buildingSelection';
import ContractAmountFields from '../../components/contracts/ContractAmountFields';
import ContractFileField, { PendingContractFile } from '../../components/contracts/ContractFileField';
import { ContractFile } from '../../api/contractsApi';
import { formatContractAmountField, parseContractAmountField } from '../../utils/contractAmount.util';
import { saveContractWithPendingFiles } from '../../utils/contractSave';
import { isCasualParkingFeeCode } from '../../utils/feeCategoryCodeGrouping';

interface AddEditContractPageProps {
  contractType: ContractType;
}

const AddEditContractPage: React.FC<AddEditContractPageProps> = ({ contractType }) => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const isRevenue = contractType === 'REVENUE';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [feeCategories, setFeeCategories] = useState<FeeCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingFiles, setExistingFiles] = useState<ContractFile[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingContractFile[]>([]);
  const [deletingFileIds, setDeletingFileIds] = useState<string[]>([]);

  const [form, setForm] = useState({
    buildingId: '',
    code: '',
    name: '',
    notes: '',
    customerName: '',
    vendorName: '',
    feeCategoryId: '',
    valueBeforeVat: '',
    valueWithVat: '',
    totalDiscount: '',
    totalVat: '',
    vatPercent: '10',
    startDate: '',
    endDate: '',
    liquidatedDate: '',
    contractTerm: '',
    signedDate: '',
    status: 'PENDING_SIGNATURE',
    contractKind: '',
    templateCode: '',
    approvalWorkflow: '',
    isVerified: false,
    parentContractCode: '',
    paymentCycleMonths: '1',
    collectionTiming: 'PREPAID' as 'PREPAID' | 'POSTPAID',
    apartmentIds: [] as string[],
  });

  useEffect(() => {
    init();
  }, [id]);

  useEffect(() => {
    if (form.buildingId) {
      loadApartments(form.buildingId);
      loadFeeCategories(form.buildingId);
    }
  }, [form.buildingId]);

  const init = async () => {
    setLoading(true);
    try {
      const buildingList = await buildingsApi.getBuildings();
      setBuildings(buildingList);
      const defaultBuildingId = applyDefaultBuildingSelection(buildingList, user, '');

      if (isEdit && id) {
        const contract = await contractsApi.getById(id);
        setExistingFiles(contract.files || []);
        setPendingFiles([]);
        setDeletingFileIds([]);
        setForm({
          buildingId: contract.buildingId,
          code: contract.code,
          name: contract.name,
          notes: contract.notes || '',
          customerName: contract.customerName || '',
          vendorName: contract.vendorName || '',
          feeCategoryId: contract.feeCategoryId || '',
          valueBeforeVat: formatContractAmountField(contract.valueBeforeVat),
          valueWithVat: formatContractAmountField(contract.valueWithVat),
          totalDiscount: formatContractAmountField(contract.totalDiscount),
          totalVat: formatContractAmountField(contract.totalVat),
          vatPercent: String(contract.vatPercent),
          startDate: contract.startDate?.slice(0, 10) || '',
          endDate: contract.endDate?.slice(0, 10) || '',
          liquidatedDate: contract.liquidatedDate?.slice(0, 10) || '',
          contractTerm: contract.contractTerm || '',
          signedDate: contract.signedDate?.slice(0, 10) || '',
          status: contract.status,
          contractKind: contract.contractKind || '',
          templateCode: contract.templateCode || '',
          approvalWorkflow: contract.approvalWorkflow || '',
          isVerified: contract.isVerified,
          parentContractCode: contract.parentContractCode || '',
          paymentCycleMonths: String(contract.paymentCycleMonths),
          collectionTiming: contract.collectionTiming ?? 'PREPAID',
          apartmentIds: contract.apartments?.map((a) => a.apartment.id) || [],
        });
      } else {
        setForm((prev) => ({ ...prev, buildingId: defaultBuildingId }));
      }
    } catch {
      toast.error('Không thể tải dữ liệu hợp đồng');
    } finally {
      setLoading(false);
    }
  };

  const loadApartments = async (buildingId: string) => {
    const data = await apartmentsApi.getApartments({ buildingId, limit: 500 });
    setApartments(Array.isArray(data) ? data : []);
  };

  const loadFeeCategories = async (buildingId: string) => {
    const data = await feeCategoriesApi.getAll(buildingId);
    const prefix = isRevenue ? 'T-KTDV' : 'C-CP';
    setFeeCategories(
      data.filter(
        (fc) =>
          fc.feeCode?.startsWith(prefix) ||
          fc.feeCode === 'KTDV' ||
          isCasualParkingFeeCode(fc.feeCode) ||
          fc.feeCode?.startsWith('C-CPBT'),
      ),
    );
  };

  const handleChange = (field: string, value: string | boolean | string[]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleApartment = (apartmentId: string) => {
    setForm((prev) => ({
      ...prev,
      apartmentIds: prev.apartmentIds.includes(apartmentId)
        ? prev.apartmentIds.filter((id) => id !== apartmentId)
        : [...prev.apartmentIds, apartmentId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.buildingId || !form.code.trim() || !form.name.trim() || !form.startDate) {
      toast.error('Vui lòng điền các trường bắt buộc');
      return;
    }

    const payload: CreateContractPayload = {
      buildingId: form.buildingId,
      contractType,
      code: form.code.trim(),
      name: form.name.trim(),
      notes: form.notes || undefined,
      customerName: isRevenue ? form.customerName || undefined : undefined,
      vendorName: !isRevenue ? form.vendorName || undefined : undefined,
      feeCategoryId: form.feeCategoryId || undefined,
      valueBeforeVat: parseContractAmountField(form.valueBeforeVat),
      valueWithVat: parseContractAmountField(form.valueWithVat),
      totalDiscount: parseContractAmountField(form.totalDiscount),
      totalVat: parseContractAmountField(form.totalVat),
      vatPercent: Number(form.vatPercent) || 0,
      startDate: form.startDate,
      endDate: form.endDate || undefined,
      liquidatedDate: form.liquidatedDate || undefined,
      contractTerm: form.contractTerm || undefined,
      signedDate: form.signedDate || undefined,
      status: form.status as CreateContractPayload['status'],
      contractKind: form.contractKind || undefined,
      templateCode: form.templateCode || undefined,
      approvalWorkflow: form.approvalWorkflow || undefined,
      isVerified: form.isVerified,
      parentContractCode: form.parentContractCode || undefined,
      paymentCycleMonths: Number(form.paymentCycleMonths) || 1,
      collectionTiming: isRevenue ? form.collectionTiming : undefined,
      apartmentIds: form.apartmentIds,
    };

    setSaving(true);
    try {
      const result = await saveContractWithPendingFiles({
        isEdit: Boolean(isEdit && id),
        contractId: id,
        payload,
        pendingFiles,
      });
      if (result === 'contract_failed') {
        return;
      }
      if (result === 'saved') {
        toast.success(isEdit ? 'Đã cập nhật hợp đồng' : 'Đã tạo hợp đồng');
      }
      navigate(isRevenue ? '/contracts/revenue' : '/contracts/expenditure');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Đang tải...</div>;
  }

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">
        {isEdit ? 'Sửa' : 'Thêm'} {isRevenue ? 'hợp đồng thu' : 'hợp đồng chi'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Mã hợp đồng *</label>
            <input className="w-full border rounded px-3 py-2" value={form.code} onChange={(e) => handleChange('code', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tên hợp đồng *</label>
            <input className="w-full border rounded px-3 py-2" value={form.name} onChange={(e) => handleChange('name', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Mã hợp đồng gốc</label>
            <input className="w-full border rounded px-3 py-2" value={form.parentContractCode} onChange={(e) => handleChange('parentContractCode', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Mã danh mục phí</label>
            <select className="w-full border rounded px-3 py-2" value={form.feeCategoryId} onChange={(e) => handleChange('feeCategoryId', e.target.value)}>
              <option value="">— Chọn —</option>
              {feeCategories.map((fc) => (
                <option key={fc.id} value={fc.id}>{fc.feeCode} — {fc.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{isRevenue ? 'Tên khách hàng' : 'Nhà cung cấp'}</label>
            <input
              className="w-full border rounded px-3 py-2"
              value={isRevenue ? form.customerName : form.vendorName}
              onChange={(e) => handleChange(isRevenue ? 'customerName' : 'vendorName', e.target.value)}
            />
          </div>
          <ContractAmountFields
            values={{
              valueBeforeVat: form.valueBeforeVat,
              valueWithVat: form.valueWithVat,
              vatPercent: form.vatPercent,
              totalDiscount: form.totalDiscount,
              totalVat: form.totalVat,
            }}
            onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
          />
          <ContractFileField
            existingFiles={existingFiles}
            pendingFiles={pendingFiles}
            onPendingFilesChange={setPendingFiles}
            onDeleteExisting={id ? async (fileId) => {
              setDeletingFileIds((prev) => [...prev, fileId]);
              try {
                await contractsApi.removeContractFile(id, fileId);
                setExistingFiles((prev) => prev.filter((f) => f.id !== fileId));
                toast.success('Đã xóa file');
              } catch {
                toast.error('Không thể xóa file');
              } finally {
                setDeletingFileIds((prev) => prev.filter((fid) => fid !== fileId));
              }
            } : undefined}
            deletingFileIds={deletingFileIds}
          />
          <div>
            <label className="block text-sm font-medium mb-1">Ngày bắt đầu *</label>
            <input type="date" className="w-full border rounded px-3 py-2" value={form.startDate} onChange={(e) => handleChange('startDate', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Ngày kết thúc</label>
            <input type="date" className="w-full border rounded px-3 py-2" value={form.endDate} onChange={(e) => handleChange('endDate', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Ngày thanh lý</label>
            <input type="date" className="w-full border rounded px-3 py-2" value={form.liquidatedDate} onChange={(e) => handleChange('liquidatedDate', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Ngày ký</label>
            <input type="date" className="w-full border rounded px-3 py-2" value={form.signedDate} onChange={(e) => handleChange('signedDate', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Kỳ thanh toán (Tháng)</label>
            <input type="number" min={1} className="w-full border rounded px-3 py-2" value={form.paymentCycleMonths} onChange={(e) => handleChange('paymentCycleMonths', e.target.value)} />
          </div>
          {isRevenue ? (
            <div>
              <label className="block text-sm font-medium mb-1">Hình thức thu</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={form.collectionTiming}
                onChange={(e) => handleChange('collectionTiming', e.target.value)}
              >
                <option value="PREPAID">Thu trước (kỳ = tháng bắt đầu HĐ)</option>
                <option value="POSTPAID">Thu sau (kỳ = cuối chu kỳ đầu)</option>
              </select>
            </div>
          ) : null}
          <div>
            <label className="block text-sm font-medium mb-1">Trạng thái</label>
            <select className="w-full border rounded px-3 py-2" value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
              <option value="PENDING_SIGNATURE">Chờ ký</option>
              <option value="SIGNED">Đã ký</option>
              <option value="LIQUIDATED">Đã thanh lý</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Loại hợp đồng</label>
            <input className="w-full border rounded px-3 py-2" value={form.contractKind} onChange={(e) => handleChange('contractKind', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Mẫu hợp đồng</label>
            <input className="w-full border rounded px-3 py-2" value={form.templateCode} onChange={(e) => handleChange('templateCode', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Ghi chú</label>
            <textarea className="w-full border rounded px-3 py-2" rows={2} value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Mã căn hộ (chọn nhiều)</label>
          <div className="border rounded p-3 max-h-48 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-2">
            {apartments.map((apt) => (
              <label key={apt.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.apartmentIds.includes(apt.id)}
                  onChange={() => toggleApartment(apt.id)}
                />
                {apt.code}
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isVerified} onChange={(e) => handleChange('isVerified', e.target.checked)} />
          Đã xác thực
        </label>

        <div className="flex gap-3 pt-4">
          <button type="submit" disabled={saving} className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50">
            {saving ? 'Đang lưu...' : 'Lưu'}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border rounded">
            Hủy
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddEditContractPage;
